/**
 * The single source of truth for how the Nikon Z fc behaves.
 *
 * `resolve()` is a pure function: given the physical position of every dial
 * and lever, it returns what the camera would actually do — which controls
 * are live, which are dead and why, and the exposure that results.
 *
 * Everything in the app (simulator, recipes, scenarios) reads from this.
 * If the camera's behaviour is wrong anywhere, it is wrong here.
 */

import {
  FLASH_SYNC_SECONDS,
  ISO_BY_ID,
  SHUTTER_BY_ID,
  THIRD_STEP_RANGE,
  formatAperture,
  formatShutter,
  snapToFNumber,
  snapToShutter,
} from './dials'
import { KIT_16_50, handheldThresholdSeconds, maxApertureAt, minApertureAt, type Lens } from './lens'
import type {
  CameraState,
  ControlId,
  ControlStatus,
  ResolvedCamera,
  Warning,
} from './types'

/** Auto ISO won't climb past this by default. */
const AUTO_ISO_CEILING = 6400
const AUTO_ISO_SERIES = [100, 200, 400, 800, 1600, 3200, 6400]

/** Slowest speed the camera will select on its own in an auto mode. */
const AUTO_SLOWEST = 30
const MECHANICAL_FASTEST = 1 / 4000

/** Above this f-number, diffraction starts visibly softening a DX sensor. */
const DIFFRACTION_THRESHOLD = 11

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Exposure value of a given aperture/shutter pair. EV = log2(N² / t). */
function evOf(fNumber: number, seconds: number): number {
  return Math.log2((fNumber * fNumber) / seconds)
}

/** Shutter time that correctly exposes at this aperture, for a target EV. */
function shutterFor(fNumber: number, targetEv: number): number {
  return (fNumber * fNumber) / Math.pow(2, targetEv)
}

/** Aperture that correctly exposes at this shutter time, for a target EV. */
function apertureFor(seconds: number, targetEv: number): number {
  return Math.sqrt(seconds * Math.pow(2, targetEv))
}

/**
 * The shutter speed the dial is actually asking for, or null when the dial
 * position has no fixed time (B, T, 1/3STEP).
 */
function dialShutterSeconds(state: CameraState): number | null {
  if (state.shutterDial === '1/3STEP') {
    return snapToShutter(
      clamp(
        state.mainCommandDialShutterSeconds,
        THIRD_STEP_RANGE.fastest,
        THIRD_STEP_RANGE.slowest,
      ),
    )
  }
  return SHUTTER_BY_ID.get(state.shutterDial)?.seconds ?? null
}

/** Camera-selected speeds land on real detents, never on arbitrary values. */
function autoShutter(seconds: number): number {
  return snapToShutter(clamp(seconds, MECHANICAL_FASTEST, AUTO_SLOWEST))
}

export function resolve(state: CameraState, lens: Lens = KIT_16_50): ResolvedCamera {
  const { mode } = state
  const warnings: Warning[] = []

  const maxAperture = maxApertureAt(lens, state.focalLength)
  const minAperture = minApertureAt(lens, state.focalLength)

  // ── Exposure compensation ────────────────────────────────────────────────
  // At `C` the dial hands compensation to a command dial: the main (rear) dial
  // in AUTO and A, the sub (front) dial in P and S — whichever one isn't
  // already busy setting aperture or shutter.
  const compOnCommandDial = state.expCompDial === 'C'
  const expComp = compOnCommandDial ? state.commandDialExpComp : (state.expCompDial as number)
  const compDial: ControlId = mode === 'AUTO' || mode === 'A' ? 'mainCommandDial' : 'subCommandDial'

  // In M with a manually set ISO, compensation can't change the exposure —
  // it only shifts the meter reading.
  const compAffectsExposure = mode !== 'M'
  const effectiveComp = compAffectsExposure ? expComp : 0

  // ── ISO ──────────────────────────────────────────────────────────────────
  const isoDetent = ISO_BY_ID.get(state.isoDial)
  const isoIsAuto = mode === 'AUTO'
  const dialIso = isoDetent?.value ?? 100

  // ── Work out shutter and aperture per mode ───────────────────────────────
  let shutterSeconds: number | null
  let aperture: number
  let shutterSource: ControlId | 'camera'
  let apertureSource: ControlId | 'camera'
  let iso = isoIsAuto ? AUTO_ISO_SERIES[0] : dialIso

  const targetEvAt = (isoValue: number) =>
    state.sceneEv100 + Math.log2(isoValue / 100) - effectiveComp

  const requestedAperture = clamp(snapToFNumber(state.subCommandDialAperture), maxAperture, minAperture)

  if (mode === 'A') {
    aperture = requestedAperture
    apertureSource = 'subCommandDial'
    shutterSource = 'camera'
    shutterSeconds = autoShutter(shutterFor(aperture, targetEvAt(iso)))
  } else if (mode === 'S') {
    const dialled = dialShutterSeconds(state)
    // B and T are M-only; the camera falls back to the sync speed here.
    shutterSeconds = dialled ?? FLASH_SYNC_SECONDS
    shutterSource = state.shutterDial === '1/3STEP' ? 'mainCommandDial' : 'shutterDial'
    apertureSource = 'camera'
    aperture = clamp(snapToFNumber(apertureFor(shutterSeconds, targetEvAt(iso))), maxAperture, minAperture)
  } else if (mode === 'M') {
    const dialled = dialShutterSeconds(state)
    shutterSeconds = dialled
    shutterSource = state.shutterDial === '1/3STEP' ? 'mainCommandDial' : 'shutterDial'
    aperture = requestedAperture
    apertureSource = 'subCommandDial'
  } else {
    // AUTO and P share a program line: aim for a shutter speed about one stop
    // clear of the handshake threshold, then open or close the aperture to
    // make that work. In AUTO, raise ISO if the light runs out.
    const aimShutter = handheldThresholdSeconds(state.focalLength) / 2
    const pick = (isoValue: number) => {
      const target = targetEvAt(isoValue)
      const wanted = snapToFNumber(apertureFor(aimShutter, target))
      const chosen = clamp(wanted, maxAperture, minAperture)
      return { aperture: chosen, seconds: autoShutter(shutterFor(chosen, target)) }
    }

    let result = pick(iso)
    if (isoIsAuto) {
      for (const candidate of AUTO_ISO_SERIES) {
        if (candidate > AUTO_ISO_CEILING) break
        iso = candidate
        result = pick(candidate)
        if (result.seconds <= handheldThresholdSeconds(state.focalLength)) break
      }
    }
    aperture = result.aperture
    shutterSeconds = result.seconds
    shutterSource = 'camera'
    apertureSource = 'camera'
  }

  // ── Meter deviation ──────────────────────────────────────────────────────
  // How far off a correct exposure the resulting combination lands.
  let meterDeviationEv = 0
  if (shutterSeconds !== null) {
    meterDeviationEv = targetEvAt(iso) - evOf(aperture, shutterSeconds)
    meterDeviationEv = Math.round(meterDeviationEv * 10) / 10
  }

  // ── Control states ───────────────────────────────────────────────────────
  const controls = buildControls(state, { compOnCommandDial, compDial, compAffectsExposure })

  // ── Warnings ─────────────────────────────────────────────────────────────
  collectWarnings(warnings, {
    state,
    lens,
    shutterSeconds,
    aperture,
    iso,
    isoIsAuto,
    maxAperture,
    meterDeviationEv,
  })

  return {
    mode,
    shutter: {
      value: shutterSeconds,
      label:
        state.shutterDial === 'B' && mode === 'M'
          ? 'Bulb'
          : state.shutterDial === 'T' && mode === 'M'
            ? 'Time'
            : formatShutter(shutterSeconds),
      source: shutterSource,
    },
    aperture: { value: aperture, label: formatAperture(aperture), source: apertureSource },
    iso: {
      value: isoIsAuto ? 'auto' : iso,
      label: isoIsAuto ? `ISO-A (${iso})` : `ISO ${iso}`,
      source: isoIsAuto ? 'camera' : 'isoDial',
    },
    exposureCompensation: expComp,
    maxApertureAtFocal: maxAperture,
    minApertureAtFocal: minAperture,
    meterDeviationEv,
    controls,
    warnings,
  }
}

// ─────────────────────────────────────────────────────────────────────────────

interface CompContext {
  compOnCommandDial: boolean
  compDial: ControlId
  compAffectsExposure: boolean
}

function buildControls(state: CameraState, comp: CompContext): Record<ControlId, ControlStatus> {
  const { mode, shutterDial } = state
  const shutterDialLive = mode === 'S' || mode === 'M'
  const atThirdStep = shutterDial === '1/3STEP'
  const atBulbOrTime = shutterDial === 'B' || shutterDial === 'T'

  // Shutter-speed dial
  let shutter: ControlStatus
  if (!shutterDialLive) {
    shutter = {
      state: 'inert',
      role: 'Does nothing',
      reason:
        mode === 'A'
          ? 'Dead in A mode. You pick the aperture; the camera picks the shutter speed. Turn this all you like — nothing changes.'
          : mode === 'AUTO'
            ? 'Dead in AUTO. The camera is choosing everything.'
            : 'Dead in P. The camera picks both shutter and aperture — nudge the pair with the rear command dial instead.',
    }
  } else if (atBulbOrTime && mode !== 'M') {
    shutter = {
      state: 'inert',
      role: 'Does nothing',
      reason: `${shutterDial} only works in M. In S the camera ignores it and falls back to the flash sync speed.`,
    }
  } else if (atThirdStep) {
    shutter = {
      state: 'active',
      role: 'Hands shutter control to the rear command dial',
      reason: 'At 1/3 STEP the engraved numbers stop mattering — the rear command dial sets the speed, 1/4000 to 30s in third-stops.',
    }
  } else {
    shutter = { state: 'active', role: 'Sets shutter speed' }
  }

  // ISO dial
  const isoDial: ControlStatus =
    mode === 'AUTO'
      ? {
          state: 'inert',
          role: 'Does nothing',
          reason: 'AUTO locks ISO to automatic. Switch to P, S, A or M to take it back.',
        }
      : { state: 'active', role: 'Sets ISO' }

  // Exposure compensation dial
  let expCompDial: ControlStatus
  if (!comp.compAffectsExposure) {
    expCompDial = {
      state: 'inert',
      role: 'Shifts the meter only',
      reason:
        "In M you're setting shutter, aperture and ISO yourself, so there's nothing left to compensate. This only moves the meter needle.",
    }
  } else if (comp.compOnCommandDial) {
    expCompDial = {
      state: 'active',
      role: `Hands compensation to the ${comp.compDial === 'mainCommandDial' ? 'rear' : 'front'} command dial`,
    }
  } else {
    expCompDial = { state: 'active', role: 'Sets exposure compensation' }
  }

  // Rear (main) command dial
  let mainCommandDial: ControlStatus
  if (mode === 'P') {
    mainCommandDial = {
      state: 'active',
      role: 'Trades a faster shutter for a wider f-stop and back — same brightness, different look',
    }
  } else if ((mode === 'S' || mode === 'M') && atThirdStep) {
    mainCommandDial = { state: 'active', role: 'Sets shutter speed (1/3 stops)' }
  } else if (comp.compOnCommandDial && comp.compDial === 'mainCommandDial') {
    mainCommandDial = { state: 'active', role: 'Sets exposure compensation' }
  } else {
    mainCommandDial = {
      state: 'inert',
      role: 'Does nothing',
      reason:
        mode === 'M'
          ? 'The shutter dial owns shutter speed right now. Set it to 1/3 STEP if you want this dial to take over.'
          : 'Nothing is assigned to this dial right now.',
    }
  }

  // Front (sub) command dial
  let subCommandDial: ControlStatus
  if (mode === 'A' || mode === 'M') {
    subCommandDial = { state: 'active', role: 'Sets aperture' }
  } else if (comp.compOnCommandDial && comp.compDial === 'subCommandDial') {
    subCommandDial = { state: 'active', role: 'Sets exposure compensation' }
  } else {
    subCommandDial = {
      state: 'inert',
      role: 'Does nothing',
      reason:
        mode === 'AUTO'
          ? 'AUTO is choosing the aperture.'
          : 'The camera is choosing the aperture in this mode.',
    }
  }

  return {
    modeLever: { state: 'active', role: 'Chooses the exposure mode — this decides which other dials are live' },
    shutterDial: shutter,
    isoDial,
    expCompDial,
    mainCommandDial,
    subCommandDial,
    photoVideoLever: { state: 'active', role: 'Switches between stills and video' },
    focalRing: { state: 'active', role: 'Zooms — and changes your widest available aperture' },
  }
}

// ─────────────────────────────────────────────────────────────────────────────

interface WarningContext {
  state: CameraState
  lens: Lens
  shutterSeconds: number | null
  aperture: number
  iso: number
  isoIsAuto: boolean
  maxAperture: number
  meterDeviationEv: number
}

function collectWarnings(warnings: Warning[], ctx: WarningContext): void {
  const { state, lens, shutterSeconds, aperture, iso, maxAperture, meterDeviationEv } = ctx
  const threshold = handheldThresholdSeconds(state.focalLength)

  if (shutterSeconds !== null && shutterSeconds > threshold) {
    const stopsOver = Math.log2(shutterSeconds / threshold)
    const rescuable = lens.hasVr && stopsOver <= lens.vrStops
    warnings.push({
      level: rescuable ? 'caution' : 'problem',
      title: `${formatShutter(shutterSeconds)} is slow for ${state.focalLength}mm`,
      detail: rescuable
        ? `Slower than ${formatShutter(threshold)}, which is about as slow as you can hand-hold at this focal length. Your lens's shake reduction (marked VR, on by default) should cover it for a still subject — but it does nothing for a subject that's moving. If it's a person, they'll blur.`
        : lens.hasVr
          ? `Well slower than ${formatShutter(threshold)} — past what your lens's shake reduction can rescue. Rest the camera on something solid, or raise ISO.`
          : `Slower than ${formatShutter(threshold)}, and this lens has no stabilisation — the Z fc body has none either. Rest the camera on something solid, or raise ISO.`,
    })
  }

  if (aperture <= maxAperture + 0.001 && state.focalLength > lens.minFocal) {
    warnings.push({
      level: 'info',
      title: `Wide open at ${formatAperture(maxAperture)}`,
      detail: `That's as much light as your lens gives you at ${state.focalLength}mm. Zoom back towards ${lens.minFocal}mm and you get up to ${formatAperture(maxApertureAt(lens, lens.minFocal))}.`,
    })
  }

  if (aperture > DIFFRACTION_THRESHOLD) {
    warnings.push({
      level: 'caution',
      title: `${formatAperture(aperture)} is past the sharpness sweet spot`,
      detail:
        'Past about f/11 the whole photo goes slightly soft — a quirk of how lenses work, not your focus. f/5.6 to f/8 is the sharpest this lens gets.',
    })
  }

  if (iso >= 12800) {
    warnings.push({
      level: 'caution',
      title: `ISO ${iso} will be visibly noisy`,
      detail: 'Usable for a shot worth having, but expect grain and mushy shadows. Drop to 6400 or below if you can afford the light.',
    })
  }

  if (Math.abs(meterDeviationEv) >= 0.4) {
    // deviation = EV the scene needs − EV the settings deliver.
    // Settings delivering a *higher* EV means less light, so negative is under.
    const under = meterDeviationEv < 0
    warnings.push({
      level: Math.abs(meterDeviationEv) >= 1.5 ? 'problem' : 'caution',
      title: `${under ? 'Under' : 'Over'}exposed by ${Math.abs(meterDeviationEv).toFixed(1)} stops`,
      detail: under
        ? 'The camera has run out of room — it cannot open up or slow down any further. Raise ISO.'
        : 'Too much light for these settings. Stop down, use a faster shutter, or lower ISO.',
    })
  }

  if (state.shutterDial === 'B' && state.mode !== 'M') {
    warnings.push({
      level: 'problem',
      title: 'Bulb only works in M',
      detail: 'Move the mode lever to M, or the B position does nothing at all.',
    })
  }
}
